/**
 * Единый список площадок фронта.
 *
 * Раньше он был скопирован в Terminal / MobileTerminal / Alerts из «большого»
 * проекта на 30 бирж — в выпадашках висели toobit, nado, paradex и прочие,
 * которых в этой сборке нет, а восьми спот-площадок наоборот не было. Теперь
 * порядок и состав держим в ОДНОМ месте, а в рантайме предпочитаем список с
 * бэка (`/api/exchanges` = `state.SUPPORTED_EXCHANGES`).
 */

/** Перпы для ВЫПАДАШЕК и фильтров. Builder-дексов HL здесь нет нарочно:
 *  наружу это одна биржа Hyperliquid (бэкенд-фильтры «hyperliquid» покрывают
 *  и дексы), а в данных пары они приходят как hl_* и показываются с пометкой
 *  декса (см. HL_DEX_VENUES и ExchangeIcon). */
/** USDC-маржинальные и COIN-M (инверсные) перпы — отдельные площадки матрицы,
 *  только данные (state.MARGIN_VENUES). Имя `<биржа>_usdc` / `<биржа>_coinm`,
 *  у Bybit — `bybit_inverse`. Порядок — по числу пар. */
export const MARGIN_VENUES = [
  'mexc_usdc', 'bybit_usdc', 'bitget_usdc', 'binance_usdc',
  'bybit_inverse', 'binance_coinm', 'okx_coinm', 'phemex_usdc',
  'mexc_coinm', 'phemex_coinm', 'kucoin_usdc', 'kucoin_coinm',
  'kraken_coinm', 'gate_coinm',
] as const;

const MARGIN_RE = /_(usdc|coinm|inverse)$/i;

/** USDC-M/COIN-M сегмент биржи? (`binance_usdc`, `bybit_inverse`, ...) */
export const isMarginVenue = (ex: string): boolean => MARGIN_RE.test(ex);

/** Базовая биржа сегмента: `binance_coinm` → `binance` (иконка, порядок). */
export const marginBaseExchange = (ex: string): string => ex.toLowerCase().replace(MARGIN_RE, '');

/** Вид сегмента для метки: `usdc` или `coin` (инверсный/COIN-M). */
export const marginKind = (ex: string): 'usdc' | 'coin' | null =>
  !isMarginVenue(ex) ? null : /_usdc$/i.test(ex) ? 'usdc' : 'coin';

export const PERP_EXCHANGES = [
  'binance', 'bybit', 'okx', 'gate', 'mexc', 'bitget', 'phemex', 'aster',
  'kucoin', 'hyperliquid', 'lighter', 'grvt', 'lbank', 'risex', 'orderly', 'edgex', 'edgex_arc',
  // Lighter на Robinhood Chain (котировка USDG) — отдельная площадка.
  'lighterhood',
  // Arcus (перп-DEX на Robinhood Chain, расчёт в USDG).
  'arcus',
  // Extended (перп-DEX на Starknet, расчёт в USDC).
  'extended',
  // Только данные (без ключей и ордеров) — как в state.SUPPORTED_EXCHANGES.
  'kraken', 'apex',
  // Crypto.com Exchange: перпы XXXUSD-PERP, только данные.
  'cryptocom',
  // USDC-M и COIN-M сегменты (тоже только данные).
  ...MARGIN_VENUES,
] as const;

/** Builder-дексы Hyperliquid — отдельные площадки МАТРИЦЫ (иначе спред
 *  «hyna ↔ основной HL» по одному символу не существует), но не фильтров. */
export const HL_DEX_VENUES = ['hl_xyz', 'hl_para', 'hl_mkts', 'hl_io'] as const;

/** Builder-декс HL? (`hl_xyz` и т.п.) */
export const isHlDexVenue = (ex: string): boolean => /^hl_/i.test(ex);

/** Каноническое имя для ФИЛЬТРОВ: дексы HL — это одна биржа hyperliquid.
 *  Пользователь, выключивший Hyperliquid чипом, выключил и её дексы. */
export const venueFamily = (ex: string): string =>
  isHlDexVenue(ex) ? 'hyperliquid' : ex;

/** Спот-площадки (`state.SPOT_EXCHANGES`). Идут после перпов: в терминале
 *  чаще выбирают фьючерс, спот — вторая нога. */
export const SPOT_VENUES = [
  'binance_spot', 'bybit_spot', 'okx_spot', 'gate_spot',
  'mexc_spot', 'bitget_spot', 'kucoin_spot', 'lbank_spot',
  'hyperliquid_spot', 'kraken_spot', 'nonkyc_spot', 'nonkycusdc_spot', 'darkex_spot', 'backpack_spot', 'lighter_spot', 'dexsol_spot', 'dexbsc_spot', 'dexbase_spot', 'dexegld_spot', 'dexada_spot', 'dexavax_spot', 'dexsui_spot', 'dexnear_spot', 'cryptocom_spot', 'coinbase_spot', 'binalpha',
] as const;

/** Fallback-состав и, главное, ПОРЯДОК отображения. */
export const ALL_EXCHANGES: string[] = [...PERP_EXCHANGES, ...SPOT_VENUES];

/** Порядок сортировки: дексы HL встают сразу за hyperliquid, USDC-M/COIN-M
 *  сегменты — сразу за своей биржей: в списках, собранных из живых данных
 *  (availableExchanges пары), они рядом с базой. */
const ORDER_LIST: string[] = ALL_EXCHANGES
  .filter((n) => !isMarginVenue(n))
  .flatMap((n) => n === 'hyperliquid'
    ? [n, ...HL_DEX_VENUES]
    : [n, ...MARGIN_VENUES.filter((m) => marginBaseExchange(m) === n)]);

const ORDER = new Map(ORDER_LIST.map((n, i) => [n, i]));

/** Сортирует произвольный набор имён в наш порядок; незнакомые — в конец
 *  по алфавиту (чтобы новая биржа с бэка не пропала из UI). */
export function orderExchanges(names: Iterable<string>): string[] {
  return [...new Set(names)].sort((a, b) => {
    const ia = ORDER.has(a) ? ORDER.get(a)! : Number.MAX_SAFE_INTEGER;
    const ib = ORDER.has(b) ? ORDER.get(b)! : Number.MAX_SAFE_INTEGER;
    return ia !== ib ? ia - ib : a.localeCompare(b);
  });
}
